import { FC, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { GuardrailComposition } from "../../../../../http/border/types/guardrailCompositions";
import { useGuardrailCompositionsApi } from "../../../../../http/border/useGuardrailCompositions";
import { PaginationQuery } from "../../../../../types";
import { formatBoolean } from "../../../../../utils";
import Button from "../../../../ui/Button";
import FlexContent from "../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../ui/InlineOrderEditor";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import NewGuardrailComposition from "./NewGuardrailComposition";

type Props = {
  // Header actions (search + create) are only published to the shared Header while
  // this tab is the active one, since Tabs keeps every tab's content mounted at once.
  active?: boolean;
};

const GuardrailCompositions: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const navigate = useNavigate();
  const { systemId } = useParams<{ systemId: string }>();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "guardrailCompositions" });
  const aborter = useRef(new AbortController());
  const guardrailCompositionsApi = useGuardrailCompositionsApi();
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "guardrailCompositions");

  const isActiveTab = active ?? true;

  const updateVisibility = async (id: string, visibility: VisibilityScopeValue) => {
    try {
      await guardrailCompositionsApi.updateAdminGuardrailCompositionVisibility(id, { visibility });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (id: string, newOrder: number) => {
    try {
      await guardrailCompositionsApi.updateAdminGuardrailCompositionOrder(id, { order: newOrder });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const [showNewTypeDialog, setShowNewTypeDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<GuardrailComposition>();
  const { accessesAction, accessesDialog } = useEntityAccesses<GuardrailComposition>("border.guardrail_composition");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = guardrailCompositionsApi.useAdminGuardrailCompositions(query, aborter.current.signal);

  const detailUrl = (id: string) => `/admin/systems/${systemId}/guardrail_compositions/${id}`;

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.guardrailCompositions.plural);
    setActions(
      <>
        <SearchBar
          placeholder={i18n.translation.guardrailCompositions.search}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <Button buttonProps={{ onClick: () => setShowNewTypeDialog(true) }}>{i18n.translation.common.createNew}</Button>
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<GuardrailComposition>
        isTall
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.common.name}</th>
            <th align="left">{i18n.translation.common.isIntermediateBarRequired}</th>
            <th align="left">{i18n.translation.common.isToeBoardRequired}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <td>{item.name}</td>
            <td>{formatBoolean(i18n.translation, item.isIntermediateBarRequired)}</td>
            <td>{formatBoolean(i18n.translation, item.isToeBoardRequired)}</td>
            <td align="center">
              <Visibility value={item.visibility} onChange={(value) => updateVisibility(item.id, value)} />
            </td>
            <td align="center">
              <InlineOrderEditor order={item.order} onUpdate={(newOrder) => updateOrder(item.id, newOrder)} />
            </td>
          </>
        )}
        onRowClick={(item) => navigate(detailUrl(item.id))}
        rowActions={(item) => [
          getEditAction(i18n, () => navigate(detailUrl(item.id))),
          accessesAction(item),
          getDeleteAction(i18n, () => setItemToDelete(item), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}

      {showNewTypeDialog && (
        <ModalDialog
          onClose={() => setShowNewTypeDialog(false)}
          title={i18n.translation.guardrailCompositions.createNew}
        >
          <NewGuardrailComposition />
        </ModalDialog>
      )}

      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.guardrailCompositions.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                guardrailCompositionsApi
                  .deleteAdminGuardrailComposition(itemToDelete.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.guardrailCompositions.questions.delete}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

export default GuardrailCompositions;
